import React, {
  FC,
  Fragment,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from "react";

import classnames from "classnames";

import { StyledStepSequence, StyledStepSequenceTrigger } from "./Styles";
import { Tooltip } from "../Tooltip";

/** Token colour role a step and its connector are painted with */
export type StepSequenceTone =
  | "success"
  | "info"
  | "warning"
  | "danger"
  | "neutral";

/** Ring style of a step: filled, toned outline or dashed outline */
export type StepSequenceFill = "solid" | "outline" | "dashed";

export interface IStepSequenceStep {
  /** Colour role of the step, resolved to theme tokens */
  tone: StepSequenceTone;
  /** How the step's ring is drawn */
  fill: StepSequenceFill;
  /** Glyph rendered inside the step; a plain dot when omitted */
  icon?: ReactNode;
  /** Animates the glyph, marking a step as the one currently running */
  pulse?: boolean;
  /** Tooltip content shown when the step is hovered; no tooltip when omitted */
  tooltip?: ReactElement | string;
  /** Tone of the connector leading into this step; the step's own tone when omitted */
  connectorTone?: StepSequenceTone;
  /** Stable key for the step; falls back to the step index */
  key?: string;
}

export interface IStepSequenceProps {
  /** Steps, rendered left to right and joined by connector lines */
  steps: IStepSequenceStep[];
  /** When provided the whole sequence is a single interactive button */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /** Accessible label, announced when the sequence is interactive */
  ariaLabel?: string;
  /** Class for the wrapper element */
  className?: string;
}

export const StepSequence: FC<IStepSequenceProps> = ({
  steps,
  onClick,
  ariaLabel,
  className,
}) => {
  if (!steps.length) {
    return null;
  }

  const content = steps.map((step, index) => (
    <Fragment key={step.key ?? `step-${index}`}>
      {index > 0 && (
        <span
          className={classnames(
            "c-step-sequence__connector",
            `c-step-sequence__connector--${step.connectorTone ?? step.tone}`
          )}
        />
      )}
      <Tooltip title={step.tooltip ?? ""} disable={!step.tooltip}>
        <span
          className={classnames(
            "c-step-sequence__step",
            `c-step-sequence__step--${step.tone}`,
            `c-step-sequence__step--${step.fill}`,
            { "c-step-sequence__step--pulse": step.pulse }
          )}
        >
          {step.icon ?? <span className="c-step-sequence__dot" />}
        </span>
      </Tooltip>
    </Fragment>
  ));

  if (onClick) {
    return (
      <StyledStepSequenceTrigger
        className={classnames("c-step-sequence", className)}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {content}
      </StyledStepSequenceTrigger>
    );
  }

  return (
    <StyledStepSequence
      className={classnames("c-step-sequence", className)}
      aria-label={ariaLabel}
    >
      {content}
    </StyledStepSequence>
  );
};

StepSequence.displayName = "StepSequence";
